import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  Observable,
  catchError,
  firstValueFrom,
  from,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { UserModel } from '../..//models/user.model';
import { AuthService } from '../../services/auth.service';
import { ChannelService } from '../../services/channel.service';
import * as channelActions from '../actions/channel.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class ChannelEffects {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  auth = inject(AuthService);
  http = inject(HttpClient);
  channelService = inject(ChannelService);
  router: Router = inject(Router);

  constructor() {}

  initChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.authenticated),
      switchMap(({ user }) => of(channelActions.loadChannel()))
    )
  );

  // effect to fetch channel from AWS using http call
  loadChannel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.authenticated),
      switchMap(({ user }) => {
        if (!user?.uid) {
          return of(
            channelActions.loadChannelSuccess({
              channel: null,
            })
          );
        }

        return from(this.channelService.getChannel(user.uid)).pipe(
          switchMap((channelObservable) =>
            channelObservable.pipe(
              map((channel) =>
                channelActions.loadChannelSuccess({
                  channel: channel.result[0] ?? null,
                })
              ),
              catchError((error) =>
                of(channelActions.loadChannelFailure({ error: error.message }))
              )
            )
          ),
          catchError((error) =>
            of(channelActions.loadChannelFailure({ error: error.message }))
          )
        );
      })
    )
  );

  // effect to fetch channel from AWS using http call
  getChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(channelActions.loadChannel),
      switchMap(async () => this.auth.getUid()),
      switchMap((uid) => {
        if (uid != null) {
          return this.channelService.getChannel(uid);
        } else {
          return of(null);
        }
      }),
      switchMap((channel$) => {
        // console.log(`Channel: ${JSON.stringify(channel.result[0])}`);
        if (channel$ === null) {
          return of(channelActions.loadChannelSuccess({ channel: null }));
        }
        return channel$.pipe(
          take(1),
          switchMap((channel) => {
            return of(
              channelActions.loadChannelSuccess({ channel: channel.result[0] })
            );
          })
        );
      })
    )
  );

  // Effect to Create the channel
  createChannel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(channelActions.createChannel),
      switchMap(async ({ channel }) => {
        const user: UserModel | null = await firstValueFrom(
          this.store.select((state) => state.user.user)
        );
        return await this.channelService.createChannel({
          ...channel,
          createdBy: user?.name,
          createdById: user?.uid,
        });
      }),
      switchMap((channel) => {
        this.router.navigate(['/channel']);
        return of(channelActions.createChannelSuccess({ channel }));
      }),
      catchError((error) => {
        return of(channelActions.createChannelFailure({ error: error }));
      })
    )
  );

  // Effect to update the channel details
  updateChannel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(channelActions.updateChannel),
      switchMap(async ({ channel }) => {
        return await this.channelService.updateChannel(channel);
      }),
      switchMap((channel) => {
        return channel.pipe(
          take(1),
          switchMap((channel) => {
            // console.log(`Update Channel Result: ${JSON.stringify(channel)}`);
            return of(
              channelActions.updateChannelSuccess({ channel: channel })
            );
          })
        );
      }),
      catchError((error) => {
        return of(channelActions.updateChannelFailure({ error: error }));
      })
    )
  );

  // Effect to delete channel
  deleteChannel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(channelActions.deleteChannel),
      switchMap(
        async ({ id }) =>
          await this.channelService
            .delete(id)
            .then(() => {
              return channelActions.deleteChannelSuccess();
            })
            .catch((error) => {
              return channelActions.deleteChannelFailure({ error: error });
            })
      )
    )
  );

  clearChannel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        return of(channelActions.clear());
      })
    )
  );
}
